<script setup lang="ts">
import type { IEmits, IProps } from './career-searchBar.types'
import { AREAS, CITIES } from './career-searchBar.consts'
import UiButton from '@/components/UiButton/UiButton.vue'
import MultiChoiceDropdown from '~/domains/career/components/career-multiChoiceDropdown/career-multiChoiceDropdown.vue'

const showCities = ref<boolean>(false)
const showAreas = ref<boolean>(false)
const form = ref<{ position: string; keywords: string; cities: string[]; areas: string[] }>({
  position: '',
  keywords: '',
  cities: [],
  areas: [],
})

const showMore = ref<Record<string, boolean>>({})

const areaLabel = computed(() => form.value.areas.length > 1 ? `${form.value.areas[0]} +${form.value.areas.length - 1}` : form.value.areas[0])
const cityLabel = computed(() => form.value.cities.length > 1 ? `${form.value.cities[0]} +${form.value.cities.length - 1}` : form.value.cities[0])

AREAS.forEach((a) => {
  showMore.value[a.key] = false
})

defineProps<IProps>()
const emit = defineEmits<IEmits>()

const selectCity = (value: string) => {
  const isAll = CITIES.some(c => c.label === value && !c.key)
  if (isAll) {
    if (form.value.cities.length) {
      form.value.cities = []
    } else {
      form.value.cities = CITIES.filter(c => c.key).map(c => c.key)
    }
  } else if (!form.value.cities.includes(value)) {
    form.value.cities.push(value)
  } else {
    form.value.cities = form.value.cities.filter((c: string) => c !== value)
  }
}

const selectArea = (value: string) => {
  if (!form.value.areas.includes(value)) {
    form.value.areas.push(value)
  } else {
    form.value.areas = form.value.areas.filter((c: string) => c !== value)
  }
}

const closeOptions = () => {
  showCities.value = false
  showAreas.value = false
}

const onSearchHandler = () => {
  closeOptions()
  emit('submit', form.value)
}

const onGoToAllHandler = () => {
  emit('showAll')
}

</script>

<template>
  <div
    class="Search-Bar"
    :class="{'Search-Bar--left': headerPosition === 'left'}"
    @click.self="closeOptions"
  >
    <div
      class="Search-Bar__title"
    >
      <h2>{{ header }}</h2>
    </div>

    <div
      class="Search-Bar__container"
      @click.self="closeOptions"
    >
      <div class="field">
        <label
          for="position"
          class="label"
        >
          Stanowisko
        </label>

        <div
          class="input"
        >
          <input
            id="position"
            v-model="form.position"
            name="position"
            type="text"
            placeholder="Np. Researcher"
          >
        </div>
      </div>

      <div class="field">
        <label
          for="keywords"
          class="label"
        >
          Słowa kluczowe
        </label>

        <div
          class="input"
        >
          <input
            id="keywords"
            v-model="form.keywords"
            name="keywords"
            type="text"
            placeholder="Np. UX"
          >
        </div>
      </div>

      <div class="field">
        <label class="label">Obszar</label>
        
        <MultiChoiceDropdown
          :options="AREAS"
          :model-value="form.areas"
          :label="areaLabel"
          @input="selectArea"
        />
      </div>

      <div class="field">
        <label class="label">Miasto</label>

        <MultiChoiceDropdown
          :options="CITIES"
          :model-value="form.cities"
          :label="cityLabel"
          @input="selectCity"
        />
      </div>

      <div class="Search-Bar__actions">
        <UiButton @clicked="onSearchHandler">
          Szukaj
        </UiButton>

        <UiButton
          variant="secondary"
          @clicked="onGoToAllHandler"
        >
          Pokaż wszystkie
        </UiButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.Search-Bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4.8rem;
  max-width: 133.9rem;
  width: 100%;
  margin: 0 auto;
  padding: 6.4rem 2.4rem 2.4rem;

  &__title {
    width: 100%;

    h2 {
      text-align: center;
      @include h7;
    }
  }

  &--left {
    gap: 3.2rem;

    h2 {
      text-align: left;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 2.4rem;
    width: 100%;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    margin-top: 0.8rem;
  }

  .field {
    display: flex;
    flex: 1;
    flex-direction: column;

    .label {
      display: inline-block;
      margin-bottom: 0.4rem;
      margin-left: 0.4rem;
      color: #696676;
      font-size: 1.4rem;
      line-height: 2rem;
    }

    .input {
      position: relative;
      width: 100%;
      max-height: 4.8rem;
      height: 4.8rem;
      background-color: #ffffff;
      border: 0.1rem solid #d0dbda;
      border-radius: 1.6rem;

      input {
        width: 100%;
        height: 100%;
        padding: 1.2rem 1.6rem;
        font-weight: 400;
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }
  }
}

@include atS {
  .Search-Bar {
    padding: 6.4rem 1rem 5.6rem;

    &__container {
      flex-direction: row;
      align-items: flex-end;
      gap: 1.6rem;
    }

    &__actions {
      margin-top: 0;
      margin-left: auto;
    }
  }
}
</style>
